import React, { useState } from 'react';
import { Cpu, Zap, Briefcase, Users, Stethoscope, Megaphone } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
// Course Card Component
const CategoryCourseCard = ({ imageUrl, title, description, id, link, label }) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardStyle = {
    width: '100%',
    background: '#ffffff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: isHovered
      ? '0 20px 40px rgba(11,49,97,0.14)'
      : '0 8px 24px rgba(11,49,97,0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '380px',
    border: '1px solid rgba(11,49,97,0.06)',
  };

  const mediaStyle = {
    width: '100%',
    height: '220px',
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #eef6ff 0%, #f8fafc 100%)',
  };

  const imgStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.4s ease',
    transform: isHovered ? 'scale(1.08)' : 'scale(1)',
  };

  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(11,49,97,0) 0%, rgba(11,49,97,0.05) 100%)',
  };

  const badgeStyle = {
    position: 'absolute',
    top: '14px',
    left: '14px',
    background: 'rgba(253, 214, 74, 0.95)',
    backdropFilter: 'blur(10px)',
    color: '#081426',
    padding: '6px 14px',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 700,
    boxShadow: '0 4px 12px rgba(253, 214, 74, 0.3)',
    zIndex: 2,
  };

  const bodyStyle = {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    flex: 1,
  };

  const titleStyle = {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#0b3161',
    margin: 0,
    lineHeight: 1.3,
  };

  const descStyle = {
    fontSize: '0.95rem',
    color: '#64748b',
    margin: 0,
    lineHeight: 1.6,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    flex: 1,
  };

  const btnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(90deg, #fdd649, #f7c42d)',
    border: 'none',
    color: '#081426',
    fontWeight: 700,
    padding: '12px 20px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    transition: 'all 0.3s ease',
    boxShadow: '0 6px 18px rgba(250, 186, 40, 0.15)',
    marginTop: 'auto',
    width: '100%',
    justifyContent: 'center',
  };

  return (
    <article
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={mediaStyle}>
        <div style={badgeStyle}>Featured</div>
        <img src={imageUrl} alt={title} style={imgStyle} />
        <div style={overlayStyle} />
      </div>

      <div style={bodyStyle}>
        <h3 style={titleStyle}>{title}</h3>
        <p style={descStyle}>{description}</p>

        <button style={btnStyle} onClick={() => window.location.href = link}>
          {label || 'Explore Course'}
          <ChevronRight size={18} />
        </button>
      </div>
    </article>
  );
};

// Main Component
export default function TopCategoriesModule() {
  const [activeTab, setActiveTab] = useState('manufacturing');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  React.useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const categories = [
    {
      id: 'manufacturing',
      name: 'Manufacturing',
      icon: Cpu,
      courses: [
        {
          id: 1,
          imageUrl: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800',
          title: 'CNC Operator Turning',
          description: 'Master precision machining, tooling setup, and G-code programming for modern CNC turning operations.',
          link: '/courseDetails/3',
          label: "Enroll Now"
        },
        {
          id: 2,
          imageUrl: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800',
          title: 'VMC Programmer',
          description: 'Learn vertical machining center programming, CAM software, and advanced manufacturing techniques.',
          link: '/courseDetails/9',
          label: 'Enroll Now',
        },
        {
          id: 3,
          imageUrl: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800',
          title: 'Industrial Automation',
          description: 'Build expertise in PLC programming, robotics, and automated manufacturing systems.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 4,
          imageUrl: 'https://images.unsplash.com/photo-1581092162384-8987c1d64718?w=800',
          title: 'Quality Control Specialist',
          description: 'Master quality assurance, inspection techniques, and manufacturing compliance standards.',
          link: 'courseDetails/10',
          label: 'Enroll Now',
        },
      ],
    },
    {
      id: 'automotive',
      name: 'Automotive',
      icon: Zap,
      courses: [
        {
          id: 5,
          imageUrl: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800',
          title: 'EV Service Technician',
          description: 'Comprehensive training in electric vehicle diagnostics, battery systems, and charging infrastructure.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 6,
          imageUrl: 'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=800',
          title: 'Automotive Diagnostics',
          description: 'Advanced vehicle diagnostics, electrical systems troubleshooting, and repair techniques.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 7,
          imageUrl: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800',
          title: 'Hybrid Technology',
          description: 'Master hybrid vehicle systems, powertrain technology, and sustainable automotive solutions.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 8,
          imageUrl: 'https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=800',
          title: 'Auto Body Repair',
          description: 'Professional body work, paint technology, and collision repair expertise.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
      ],
    },
    {
      id: 'technology',
      name: 'Technology',
      icon: Briefcase,
      courses: [
        {
          id: 9,
          imageUrl: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800',
          title: 'Full Stack Development',
          description: 'Complete web development training covering frontend, backend, and database technologies.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 10,
          imageUrl: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800',
          title: 'Data Analytics',
          description: 'Learn data visualization, statistical analysis, and business intelligence tools.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 11,
          imageUrl: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800',
          title: 'Cloud Computing',
          description: 'Master AWS, Azure, and cloud architecture for modern infrastructure solutions.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 12,
          imageUrl: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800',
          title: 'Cybersecurity Essentials',
          description: 'Comprehensive security training covering threat detection and network protection.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
      ],
    },
    {
      id: 'soft-skills',
      name: 'Soft Skills',
      icon: Users,
      courses: [
        {
          id: 13,
          imageUrl: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800',
          title: 'Leadership Excellence',
          description: 'Develop leadership capabilities, team management skills, and strategic thinking.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 14,
          imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800',
          title: 'Team Collaboration',
          description: 'Master effective communication, conflict resolution, and collaborative work strategies.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 15,
          imageUrl: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800',
          title: 'Time Management Pro',
          description: 'Optimize productivity with proven time management and organizational techniques.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 16,
          imageUrl: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800',
          title: 'Professional Communication',
          description: 'Enhance business communication, presentation skills, and professional networking.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
      ],
    },

    // === New: Healthcare ===
    {
      id: 'healthcare',
      name: 'Healthcare',
      icon: Stethoscope,
      courses: [
        {
          id: 17,
          imageUrl: 'https://images.unsplash.com/photo-1580281658629-6c66a3e66a4e?w=800',
          title: 'Certified Nursing Assistant',
          description: 'Hands-on training in patient care, basic medical procedures, and bedside assistance.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 18,
          imageUrl: 'https://images.unsplash.com/photo-1580281657521-8b4b6f34b9d9?w=800',
          title: 'Medical Coding & Billing',
          description: 'Learn ICD-10, CPT coding, claims processing and healthcare billing workflows.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 19,
          imageUrl: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
          title: 'Phlebotomy Technician',
          description: 'Master blood collection techniques, specimen handling, and safety protocols.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 20,
          imageUrl: 'https://images.unsplash.com/photo-1576765974023-0535e5e8e6f5?w=800',
          title: 'Healthcare Administration',
          description: 'Operations, compliance, and management skills needed to run healthcare facilities efficiently.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
      ],
    },

    // === New: Digital Marketing ===
    {
      id: 'digital-marketing',
      name: 'Digital Marketing',
      icon: Megaphone,
      courses: [
        {
          id: 21,
          imageUrl: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800',
          title: 'SEO Specialist',
          description: 'Learn keyword research, on-page & off-page SEO, and technical SEO best practices.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 22,
          imageUrl: 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?w=800',
          title: 'Social Media Marketing',
          description: 'Create winning social strategies, content calendars, and audience growth techniques.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 23,
          imageUrl: 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?w=800&ixlib=rb-1.2.1&q=80&fm=jpg&crop=entropy&cs=tinysrgb&w=800',
          title: 'Content Marketing & Copywriting',
          description: 'Craft high-converting copy, storytelling techniques, and content distribution tactics.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
        {
          id: 24,
          imageUrl: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=800',
          title: 'PPC & Google Ads',
          description: 'Manage paid campaigns, conversion tracking, bidding strategies, and ROI optimization.',
          link: '/allCourses/all',
          label: 'Coming Soon',
        },
      ],
    },
  ];


  const activeCategory = categories.find(cat => cat.id === activeTab);

  const wrapStyle = {
    width: '100%',
    padding: '70px 0',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
  };

  const containerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 24px',
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '48px',
  };

  const eyebrowStyle = {
    color: '#0b3161',
    fontWeight: 700,
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginBottom: '12px',
  };

  const headingStyle = {
    fontFamily: "'Roboto Slab', serif",
    color: '#081426',
    fontSize: isDesktop ? '2.8rem' : '2rem',
    margin: '0 0 16px 0',
    lineHeight: 1.1,
    fontWeight: 700,
  };

  const descStyle = {
    color: '#64748b',
    fontSize: '1.1rem',
    margin: '0 auto',
    lineHeight: 1.7,
    maxWidth: '720px',
  };

  const tabsContainerStyle = {
    display: 'flex',
    gap: isDesktop ? '12px' : '8px',
    marginBottom: '40px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: isDesktop ? '0 80px' : '0',
  };

  const tabStyle = (isActive) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: isDesktop ? '14px 28px' : '12px 20px',
    borderRadius: '12px',
    border: 'none',
    background: isActive
      ? 'linear-gradient(135deg, #0b3161 0%, #1e4a7a 100%)'
      : '#ffffff',
    color: isActive ? '#ffffff' : '#475569',
    fontWeight: 700,
    fontSize: isDesktop ? '1rem' : '0.9rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: isActive
      ? '0 8px 24px rgba(11, 49, 97, 0.2)'
      : '0 4px 12px rgba(11, 49, 97, 0.06)',
    transform: isActive ? 'translateY(-2px)' : 'translateY(0)',
  });

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: isDesktop
      ? 'repeat(auto-fit, minmax(280px, 1fr))'
      : window.innerWidth > 500 ? 'repeat(2, 1fr)' : '1fr',
    gap: '24px',
    marginBottom: '48px',
  };

  const viewAllStyle = {
    textAlign: 'center',
  };

  const viewAllBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    background: 'transparent',
    border: '2px solid #0b3161',
    color: '#0b3161',
    fontWeight: 700,
    padding: '14px 32px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
  };

  return (
    <section style={wrapStyle}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={eyebrowStyle}>Explore by Category</div>
          <h2 style={headingStyle}>Top Training Categories</h2>
          <p style={descStyle}>
            Discover career-focused programs designed by industry experts. From technical skills to professional development,
            find the perfect path to advance your career and achieve your goals.
          </p>
        </div>

        {/* Tabs */}
        <div style={tabsContainerStyle}>
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                style={tabStyle(activeTab === category.id)}
                onClick={() => setActiveTab(category.id)}
              >
                <Icon size={20} />
                {category.name}
              </button>
            );
          })}
        </div>

        {/* Course Grid */}
        <div style={gridStyle}>
          {activeCategory.courses.map((course) => (
            <CategoryCourseCard
              key={course.id}
              imageUrl={course.imageUrl}
              title={course.title}
              description={course.description}
              id={course.id}
              link={course.link}
              label={course.label}
            />
          ))}
        </div>

        {/* View All Button */}
        <div style={viewAllStyle}>
          <button style={viewAllBtnStyle} onClick={() => window.location.href = '/allCourses/all'}>
            View All  Courses
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
}